import React, { useEffect, useState } from 'react';
import { ChartBarIcon, ClockIcon, UserGroupIcon, LightBulbIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { getResourceBySlug } from '../../../lib/db';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import { InformationCircleIcon, RocketLaunchIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const StorytellingWithDataKeyInfo = () => {
  const [resource, setResource] = useState(null);

  useEffect(() => {
    getResourceBySlug('storytelling-with-data').then(setResource);
  }, []);

  return (
    <div className="space-y-8">
      <PlaybookHero
        title={resource?.title || "Storytelling with Data"}
        subtitle={resource?.description || "How To Use Data & Storytelling To Get Heard"}
        category={resource?.category || "Culture"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "5 Hours Prep | 1 hour execution"}
        type={resource?.type}
      />

      <div className="border-l-4 border-primary-light pl-4 italic text-gray-300">
        <p className="text-lg">
          This Playbook is for you if any of the following (or similar) are true:
        </p>
        <ul className="mt-4 space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You are struggling to get buy-in on ideas/initiatives/investment
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You've been told "storytelling" is an important skill for a people leader but aren't sure how to build that muscle
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You have a gut feel that something's not right, and you believe you know what might help but you're not sure how to tell that story
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            You need data to prove that whatever is happening is causing problems
          </li>
        </ul>
      </div>

      <PlaybookSection
        title="Key Info"
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="Essential Information"
      >
        <PlaybookMetrics
          metrics={[
            {
              label: "Difficulty",
              value: "Medium",
              icon: <RocketLaunchIcon className="h-6 w-6 text-primary-light" />
            },
            {
              label: "Timeline",
              value: "5 Hours Prep | 1 hour execution",
              icon: <RocketLaunchIcon className="h-6 w-6 text-primary-light" />
            },
            {
              label: "Team Size",
              value: "All",
              icon: <RocketLaunchIcon className="h-6 w-6 text-primary-light" />
            },
            {
              label: "Why Use",
              value: "If you have a clear vision and want to transform your ideas into reality, use this to guide you in effectively communicating what needs to be done.",
              icon: <RocketLaunchIcon className="h-6 w-6 text-primary-light" />
            },
            {
              label: "When to Use",
              value: "Use this playbook when you need to craft a compelling narrative backed by data to secure buy-in, investment, or support for your initiatives.",
              icon: <RocketLaunchIcon className="h-6 w-6 text-primary-light" />
            }
          ]}
        />
      </PlaybookSection>

      <PlaybookSection
        title="Watch This"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Video Overview"
      >
        <VideoEmbed
          src="https://www.loom.com/embed/ce874991105d48b38cca1a2cd4fa4cc8"
          title="Storytelling with Data Overview"
        />
      </PlaybookSection>

      <PlaybookSection
        title="What Is This"
        icon={<InformationCircleIcon className="h-7 w-7 text-primary-light" />}
        heading="Guide Overview"
      >
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300">
            Welcome to your step-by-step guide on getting heard and influencing like a beast. This playbook is all about how to tell compelling stories with data.
          </p>
        </div>
      </PlaybookSection>

      <PlaybookSection
        title="What You'll Need"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Prerequisites"
      >
        <PlaybookCard>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              A meaningful data set from within your organisation (e.g. engagement survey results or core people and talent metrics like ARR per employee over time, cost of hire etc…)
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              Some time to analyse, make a chart or 2 and put together a narrative
            </li>
          </ul>
        </PlaybookCard>
      </PlaybookSection>

      <PlaybookSection
        title="Obstacles"
        icon={<ExclamationTriangleIcon className="h-7 w-7 text-primary-light" />}
        heading="Common Challenges"
      >
        <ExpandableCallout
          title="Getting Buy-In"
          type="warning"
          defaultOpen={true}
        >
          <p className="text-gray-300">
            One of the biggest challenges is getting stakeholders to care about your data story. This often happens when the story isn't properly linked to business outcomes or when the data isn't presented in a compelling way.
          </p>
        </ExpandableCallout>

        <ExpandableCallout
          title="Data Quality"
          type="warning"
          defaultOpen={true}
        >
          <p className="text-gray-300">
            Poor quality or incomplete data can undermine your story. It's crucial to ensure your data is accurate, relevant, and properly analyzed before attempting to tell your story.
          </p>
        </ExpandableCallout>

        <ExpandableCallout
          title="Time Constraints"
          type="warning"
          defaultOpen={true}
        >
          <p className="text-gray-300">
            Finding the time to properly analyze data and craft a compelling narrative can be challenging, especially in fast-paced environments. However, investing this time upfront is crucial for success.
          </p>
        </ExpandableCallout>
      </PlaybookSection>
    </div>
  );
};

export default StorytellingWithDataKeyInfo; 